import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DepartmentService, IBaseDepartment } from '@shared/services/department.service';
import { EmployeeNoteService, IEmployeesNote } from '@shared/services/employee-note.service';
import { EmployeeService, IMesUser } from '@shared/services/mesuser.service';
import { LocalStorageService } from '@shared/services/storage.service';
import { ITeamProfile, TeamProfileService } from '@shared/services/teamProfiles.service';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { ICreateShiftNoteInput, IEmployeeItemsModel, IShiftCreationResult } from './create-shift-note.models';
import { atLeastOneRequiredValidator, sameStartAndEndTimeValidator } from './create-shift-note.validators';
import { IShiftCreationRequest, ShiftCreationOrchestratorService } from './shift-creation-orchestrator.service';

@Component({
	selector: 'app-create-shift-note',
	templateUrl: './create-shift-note.component.html',
	styleUrls: ['./create-shift-note.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateShiftNoteComponent implements OnInit, OnDestroy {
	@Output() created = new EventEmitter<IShiftCreationResult[]>();
	@ViewChild('workTaskFilterInput')
	workTaskFilterInput: ElementRef<HTMLInputElement>;

	form: FormGroup;
	loading = false;
	showResults = false;
	results: IShiftCreationResult[] = [];
	items: IEmployeeItemsModel[];
	teamProfiles: ITeamProfile[] = [];
	workTasks: IWorktask[] = [];
	filteredWorkTasks: IWorktask[] = [];
	workTaskFilter = '';

	progress = { completed: 0, total: 0 };
	progressPercent = 0;

	private static readonly STORAGE_KEY_USE_TEAM_PROFILE = 'ShiftNote#useTeamProfile';

	private static readonly STORAGE_KEY_START_TIME = 'ShiftNote#startTime';
	private static readonly STORAGE_KEY_END_TIME = 'ShiftNote#endTime';

	private destroy$ = new Subject<void>();

	constructor(
		public dialogRef: MatDialogRef<CreateShiftNoteComponent>,
		private fb: FormBuilder,
		private cdr: ChangeDetectorRef,
		private translate: TranslateService,
		private employeeService: EmployeeService,
		private departmentService: DepartmentService,
		private workTaskService: WorktaskService,
		private teamProfileService: TeamProfileService,
		private employeeNoteService: EmployeeNoteService,
		private orchestratorService: ShiftCreationOrchestratorService,
		private localStorageService: LocalStorageService,
		@Inject(MAT_DIALOG_DATA) public data: ICreateShiftNoteInput,
	) {}

	ngOnInit(): void {
		this.initializeForm();
		this.loadData();
		this.subscribeToProgress();
	}

	private initializeForm(): void {
		const savedUseTeamProfile = this.localStorageService.get(CreateShiftNoteComponent.STORAGE_KEY_USE_TEAM_PROFILE);
		const savedStartTime = this.localStorageService.get(CreateShiftNoteComponent.STORAGE_KEY_START_TIME);
		const savedEndTime = this.localStorageService.get(CreateShiftNoteComponent.STORAGE_KEY_END_TIME);

		const useTeamProfile = savedUseTeamProfile !== null ? savedUseTeamProfile === 'true' : true;
		const startTime = savedStartTime || '00:00';
		const endTime = savedEndTime || '00:00';

		this.form = this.fb.group(
			{
				startDate: [new Date(this.data.startDate), [Validators.required]],
				endDate: [new Date(this.data.endDate), [Validators.required]],
				startTime: [{ value: startTime, disabled: useTeamProfile }],
				endTime: [{ value: endTime, disabled: useTeamProfile }],
				workTask: [null as string],
				note: ['', [Validators.maxLength(512), Validators.pattern(ValidatorPatterns.Description)]],
				useTeamProfile: [useTeamProfile],
			},
			{
				validators: [atLeastOneRequiredValidator, sameStartAndEndTimeValidator],
			},
		);

		this.form
			.get('useTeamProfile')
			.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe((checked: boolean) => {
				this.localStorageService.set(CreateShiftNoteComponent.STORAGE_KEY_USE_TEAM_PROFILE, String(checked));
				if (checked) {
					this.form.get('startTime').disable();
					this.form.get('endTime').disable();
				} else {
					this.form.get('startTime').enable();
					this.form.get('endTime').enable();
				}
			});

		this.form
			.get('startTime')
			.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe((v) => this.localStorageService.set(CreateShiftNoteComponent.STORAGE_KEY_START_TIME, v));

		this.form
			.get('endTime')
			.valueChanges.pipe(takeUntil(this.destroy$))
			.subscribe((v) => this.localStorageService.set(CreateShiftNoteComponent.STORAGE_KEY_END_TIME, v));
	}

	private loadData(): void {
		forkJoin({
			employeesResult: this.employeeService.getEmployees(),
			departmentResult: this.departmentService.getDepartments(),
			workTasksResult: this.workTaskService.getWorktasks(),
			teamProfilesResult: this.teamProfileService.getTeamProfiles(),
		})
			.pipe(takeUntil(this.destroy$))
			.subscribe(({ employeesResult, departmentResult, workTasksResult, teamProfilesResult }) => {
				this.teamProfiles = teamProfilesResult.result;
				this.buildEmployeeItems(employeesResult.result, departmentResult.result);
				this.buildWorkTasks(workTasksResult.result);
			});
	}

	private buildEmployeeItems(employees: IMesUser[], departments: IBaseDepartment[]): void {
		const items: IEmployeeItemsModel[] = [];
		employees.forEach((employee) => {
			if (employee.isActive) {
				const department = departments.find((d) => d.id === employee.departmentId);
				items.push({
					group: department?.alias ?? '',
					text: `(${employee.alias}) ${employee.firstName} ${employee.lastName}`,
					selected: false,
					employeeId: employee.id,
					teamProfileId: employee.teamProfileId,
				});
			}
		});
		this.items = items.sort((a, b) => (a.group + a.text).localeCompare(b.group + b.text));
	}

	private buildWorkTasks(worktasks: IWorktask[]): void {
		this.workTasks = worktasks.filter((wt) => wt.isActive && wt.id !== WorktaskService.offWorkTaskId).sort((a, b) => a.name.localeCompare(b.name));
		this.filteredWorkTasks = [...this.workTasks];
	}

	private subscribeToProgress(): void {
		this.orchestratorService.progress$.pipe(takeUntil(this.destroy$)).subscribe((progress) => {
			this.progress = progress;
			this.progressPercent = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
			this.cdr.detectChanges();
		});
	}

	filterWorkTasks(): void {
		const filter = this.workTaskFilter.toLowerCase();
		this.filteredWorkTasks = this.workTasks.filter((wt) => wt.name.toLowerCase().includes(filter) || wt.alias.toLowerCase().includes(filter));
	}

	trackByWorkTaskId(_index: number, workTask: IWorktask): string {
		return workTask.id;
	}

	onWorkTaskSelectOpened(opened: boolean): void {
		if (opened) {
			setTimeout(() => this.workTaskFilterInput?.nativeElement?.focus());
		} else {
			this.workTaskFilter = '';
			this.filterWorkTasks();
		}
	}

	get controls() {
		return this.form.controls;
	}

	isItemSelected(): boolean {
		return this.items?.some((c) => c.selected);
	}

	get failedResults(): IShiftCreationResult[] {
		return this.results.filter((r) => r.status !== 'success');
	}

	get successResults(): IShiftCreationResult[] {
		return this.results.filter((r) => r.status === 'success');
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onCancelProcessing(): void {
		this.orchestratorService.cancel();
	}

	onClose(): void {
		this.created.emit(this.results);
		this.dialogRef.close(this.results);
	}

	onCopyToClipboard(): void {
		const datePipe = new DatePipe('en-US');
		const t = (key: string) => this.translate.instant(`mes.administration.shift-approval.create-shift-note.results.${key}`);

		const header = `${t('columns.employee')}\t${t('columns.date')}\t${t('columns.status')}\t${t('columns.reason')}`;
		const rows = this.results.map((r) => {
			const status = this.translate.instant(`mes.administration.shift-approval.create-shift-note.results.status.${r.status}`);
			const reason = r.reason ? this.translate.instant(`mes.administration.shift-approval.create-shift-note.results.reasons.${r.reason}`) : '';
			const detail = r.reasonDetail ? ` (${r.reasonDetail})` : '';
			const date = datePipe.transform(r.date, 'dd-MM-yyyy');
			return `${r.employeeName}\t${date}\t${status}\t${reason}${detail}`;
		});

		const text = [header, ...rows].join('\n');
		navigator.clipboard.writeText(text).catch(() => {});
	}

	async onSave(): Promise<void> {
		if (this.loading) return;
		if (this.form.invalid) return;
		if (!this.isItemSelected()) return;

		this.loading = true;
		const selectedEmployees = this.items.filter((c) => c.selected);
		const formValue = this.form.getRawValue();

		// Create notes if note is provided
		if (formValue.note?.trim()) {
			const employeesNote: IEmployeesNote = {
				note: formValue.note,
				startDate: new Date(formValue.startDate),
				endDate: new Date(formValue.endDate),
				employeeIds: selectedEmployees.map((e) => e.employeeId),
			};
			// Set time to midday to compensate for timezone offset shifting the date
			employeesNote.startDate.setHours(12, 0, 0);
			employeesNote.endDate.setHours(12, 0, 0);
			this.employeeNoteService.createNotes(employeesNote);
		}

		// Create shifts if work task is selected
		if (formValue.workTask) {
			this.showResults = true;
			const workTask = this.workTasks.find((wt) => wt.id === formValue.workTask);

			const request: IShiftCreationRequest = {
				employees: selectedEmployees,
				startDate: formValue.startDate,
				endDate: formValue.endDate,
				startTime: formValue.startTime,
				endTime: formValue.endTime,
				workTask,
				useTeamProfile: formValue.useTeamProfile,
				teamProfiles: this.teamProfiles,
			};

			this.results = await this.orchestratorService.executeCreate(request);
			this.loading = false;
		} else {
			// Only note was created, close dialog
			this.created.emit([]);
			this.dialogRef.close([]);
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
